// Justificación de tamaño (policies/code.md, "File size") -- ~688 líneas operativas, sobre el
// cap de 500 para shared/ui/**. Ya se extrajo lo separable (02.9-shared-widgets.md, paso b de la
// Fase 2): `TaskWizard.jsx` (wizard de 5 pasos) y `DropdownOptions.jsx` (primitivo trivial). Se
// evaluó extraer un `AreaRow.jsx`/`TaskRow.jsx` adicional para el cuerpo de la tabla (§5) pero
// se descartó: ese bloque JSX lee y escribe ~15 piezas de estado/callbacks del padre
// (`activeDropdown`/`setActiveDropdown`, `addTag`/`removeTag`, `activeWizard`/`setActiveWizard`,
// `rapidTaskAreaId`/`rapidTaskTitle`/`handleRapidTaskSubmit`, `isAdmin`,
// `updateUrlTaskParam`/`setOpenTaskId`, entre otros) -- extraerlo sería pura ceremonia de
// prop-drilling, no una encapsulación real. Lo que queda es una tabla grande con estado
// (áreas × tareas) con edición inline, tagging, resize de columnas, paginación, y persistencia
// de estado vía atributo DOM (`data-outline-state`, debounced) más un segundo contrato DOM
// independiente (`handleDeleteBlock`) -- mismo carve-out que `Map.jsx`/`inline_flow.jsx`.
import React, { useState, useEffect, useRef } from 'react';
// 02.9-shared-widgets.md paso a (§0.2/§3) -- excepción documentada, no una violación pendiente:
// aísla el overlay de tarea (`ComponentTaskHoy`, lazy, más abajo) en su propio contexto de
// router para que no compita con el router real de la app -- no lee estado de la URL real, que
// es lo que la regla busca prevenir.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: abre el panel de tarea indicado por el parámetro `?task=` de la URL una vez cargan los datos coincidentes.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const ComponentTaskHoy = React.lazy(() => import('../../../../features/tasks/components/ComponentTaskHoy'));

import { Plus, Check, MapPin, Mail, Video, Link, MessageCircle, FileText, Search, Trash2, Lock, Zap, CheckSquare } from 'lucide-react';
import { Instagram, Twitter, Linkedin, Facebook } from '../../icons/BrandIcons';
import { tasksApi } from '../../../../features/tasks/api';
import { useSpaceMembers } from '../../../../features/spaces/hooks/useSpaceDashboard';
// 02.9-shared-widgets.md paso a (§0.2) -- excepción documentada, mismo tipo que
// `CommentDraftContext` en CommentArea.jsx: `DoctexHtml.jsx` monta este widget en su propio
// `createRoot()` desconectado del árbol de la app -- no hay ningún container real desde el cual
// threadear `toast`/`user` como prop (y si lo hiciera, quedaría congelado al primer mount, ver
// abajo). Llamar los hooks acá directo es correcto, no un atajo -- y desde 03.1-auth-store.md
// (Fase 3), `useCurrentUser()` es un store zustand a nivel de módulo: funciona igual esté o no
// esté montado dentro del re-wrap local de `DoctexHtml.jsx` (§5), a diferencia del
// `useContext(UserContext)` de antes.
import { useToast } from '../../Toast/ToastProvider';
// eslint-disable-next-line no-restricted-imports -- montado vía `createRoot()` desde `DoctexHtml.jsx`, sin container real (ver arriba).
import { useAuthUser } from '../../../../context/authHooks';
import DropdownOptions from './DropdownOptions';
import TaskWizard from './TaskWizard';
import './inline_social_outline.css';

const COLORS = [
    { id: 'amarillo', hex: '#fbbf24' },
    { id: 'morado', hex: '#a855f7' },
    { id: 'naranja', hex: '#f97316' },
    { id: 'verde', hex: '#4ade80' },
    { id: 'azul', hex: '#60a5fa' },
    { id: 'rosa', hex: '#f472b6' },
    { id: 'arcoiris', hex: 'linear-gradient(45deg, #f87171, #fbbf24, #4ade80, #60a5fa, #a855f7)' },
    { id: 'gris', hex: '#9ca3af' },
    { id: 'blanco', hex: '#ffffff' }
];

const ADS_OPTIONS = ['meta', 'google', 'facebook', 'fisico', 'instagram', 'whats', 'linkedin'];
const FORMAT_OPTIONS = ['carrusel', 'video corto', 'stickers', 'social media post', 'infografia', 'video largo', 'live', 'demo', 'pregunta', 'explicacion', 'hackaton', 'como participar', 'informacion'];
const SOCIAL_ICONS = [
    { id: 'instagram', icon: <Instagram size={14} /> },
    { id: 'twitter', icon: <Twitter size={14} /> },
    { id: 'tiktok', icon: <Video size={14} /> },
    { id: 'facebook', icon: <Facebook size={14} /> },
    { id: 'linkedin', icon: <Linkedin size={14} /> },
    { id: 'correo', icon: <Mail size={14} /> },
    { id: 'blog', icon: <FileText size={14} /> },
    { id: 'fisico', icon: <MapPin size={14} /> }
];
const CTA_OPTIONS = ['responde y te envio', 'link', 'whats', 'airdrop'];



const InlineSocialOutline = ({ blockId, docId, spaceId, onDelete }) => {
    const [areas, setAreas] = useState([]);
    const [columnWidths, setColumnWidths] = useState({
        area: 160,
        pub: 280,
        content: 320,
        social: 200,
        cta: 220
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isHovered, setIsHovered] = useState(false);
    const [isBlockLoading, setIsBlockLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Rapid task state
    // 02.9-shared-widgets.md paso a (§0.2) -- reemplaza el `localStorage.getItem('currentUser')`
    // a mano (no reactivo a un login/logout en otra pestaña o una actualización de perfil) por
    // un hook directo -- ver la nota de import más arriba sobre por qué esto es la excepción
    // correcta, no un prop threading desde `DoctexHtml.jsx`. `useAuthUser()` (objeto completo)
    // porque el `ComponentTaskHoy` que se monta abajo al abrir una tarea necesita campos anchos
    // (`timezone`/`spaces`/`currentSpace`) -- 04-enforce.md 4.4 (era `useCurrentUser()` angosto).
    const currentUser = useAuthUser();
    const { data: members = [] } = useSpaceMembers({ id: spaceId });
    const currentMember = members.find(m => String(m.id) === String(currentUser?.id));
    const isAdmin = currentMember?.role === 'admin' || currentMember?.role === 'owner' || currentMember?.permissions?.is_owner;
    const [rapidTaskAreaId, setRapidTaskAreaId] = useState(null);
    const [rapidTaskTitle, setRapidTaskTitle] = useState('');

    const handleRapidTaskSubmit = async (areaId) => {
        if (!rapidTaskTitle.trim()) {
            setRapidTaskAreaId(null);
            return;
        }
        try {
            const response = await tasksApi.createTask({
                space_id: spaceId,
                title: rapidTaskTitle.trim(),
                type: 'task',
                status: 'open',
                stage: 'planeada'
            });
            
            const newTask = {
                // eslint-disable-next-line react-hooks/purity
                id: `task-${Date.now()}`,
                name: rapidTaskTitle.trim(),
                assignee: currentUser?.id ? { id: currentUser.id, name: currentUser.name || currentUser.username } : null,
                taskId: response?.task?.id || response?.id || null,
                status: 'open',
                start_time: new Date().toISOString(),
                ads: [],
                formats: [],
                viral: false,
                socials: [],
                cta: []
            };
            
            setAreas(areas.map(a => {
                if (a.id === areaId) {
                    return { ...a, tasks: [...a.tasks, newTask] };
                }
                return a;
            }));
            
            toast.success("Tarea rápida creada");
        } catch (e) {
            console.error(e);
            toast.error("Error al crear la tarea");
        }
        setRapidTaskTitle('');
        setRapidTaskAreaId(null);
    };
    const [creatingArea, setCreatingArea] = useState(false);
    const [newAreaColor, setNewAreaColor] = useState(null);
    const [newAreaTitle, setNewAreaTitle] = useState('');
    
    const headerScrollRef = useRef(null);
    const [stickyOffset, setStickyOffset] = useState(0);

    // Calc document header height to stick right below it
    useEffect(() => {
        const updateOffset = () => {
            const docTopBar = document.querySelector('.doc-top-bar');
            if (docTopBar) {
                setStickyOffset(docTopBar.offsetHeight);
            } else {
                setStickyOffset(50); // Fallback
            }
        };
        updateOffset();
        // Option to observe resize if it changes dynamically
        // eslint-disable-next-line no-restricted-syntax
        window.addEventListener('resize', updateOffset);
        return () => window.removeEventListener('resize', updateOffset);
    }, []);

    const [activeWizard, setActiveWizard] = useState(null); // { areaId }
    const [openTaskId, setOpenTaskId] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null); // { areaId, taskId, type }
    const areaInputRef = useRef(null);
    const toast = useToast();

    const handleDeleteBlock = () => {
        toast.prompt('¿Estás seguro de eliminar este bloque de la base de datos?', {
            confirmText: 'Sí, borrar',
            cancelText: 'Cancelar',
            onConfirm: () => {
                const blockEl = document.querySelector(`.doc-inline-social-outline-block[data-id="${blockId}"]`);
                if (blockEl) {
                    blockEl.remove();
                    const editor = document.querySelector('.doc-editor-content');
                    if (editor) editor.dispatchEvent(new Event('input', { bubbles: true }));
                    toast.success("Bloque eliminado permanentemente");
                }
            }
        });
    };

    // Auto-sync tasks with DB on mount
    useEffect(() => {
        let isMounted = true;
        const syncTasks = async () => {
            try {
                const allTaskIds = [];
                areas.forEach(area => {
                    area.tasks.forEach(task => {
                        if (task.taskId) allTaskIds.push(task.taskId);
                    });
                });
                
                if (allTaskIds.length === 0) return;

                const uniqueIds = [...new Set(allTaskIds)];
                const res = await fetch(`/api/tasks?ids=${uniqueIds.join(',')}`, { credentials: 'include' });
                if (!res.ok) return;
                
                const data = await res.json();
                const liveTasks = Array.isArray(data) ? data : (data.tasks || []);
                const liveTaskMap = new Map(liveTasks.map(t => [t.id, t]));

                if (!isMounted) return;

                setAreas(prevAreas => {
                    let hasChanges = false;
                    const newAreas = prevAreas.map(area => {
                        const newTasks = [];
                        for (const task of area.tasks) {
                            if (!task.taskId) {
                                newTasks.push(task);
                                continue;
                            }
                            
                            const liveTask = liveTaskMap.get(task.taskId);
                            if (!liveTask) {
                                // Task was deleted in DB
                                hasChanges = true;
                                continue;
                            }

                            let updatedTask = { ...task };
                            let taskChanged = false;
                            
                            if (updatedTask.name !== liveTask.title) {
                                updatedTask.name = liveTask.title;
                                taskChanged = true;
                            }
                            
                            const liveAssignee = typeof liveTask.assignee === 'object' ? (liveTask.assignee?.nick || liveTask.assignee?.name) : liveTask.assignee;
                            if (liveAssignee && updatedTask.assignee !== liveAssignee) {
                                updatedTask.assignee = liveAssignee;
                                taskChanged = true;
                            }

                            if (taskChanged) {
                                hasChanges = true;
                            }
                            newTasks.push(updatedTask);
                        }
                        
                        if (newTasks.length !== area.tasks.length || newTasks.some((t, i) => t !== area.tasks[i])) {
                            return { ...area, tasks: newTasks };
                        }
                        return area;
                    });

                    return hasChanges ? newAreas : prevAreas;
                });

            } catch (err) {
                console.error("Error syncing tasks in InlineSocialOutline", err);
            }
        };

        syncTasks();
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load block from API on mount
    useEffect(() => {
        let isMounted = true;
        const fetchBlock = async () => {
            try {
                const res = await fetch(`/api/blocks/${blockId}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    if (data.block && data.block.content) {
                        const content = data.block.content;
                        if (isMounted) {
                            if (content.areas) setAreas(content.areas);
                            if (content.columnWidths) setColumnWidths(content.columnWidths);
                            setIsBlockLoading(false);
                            setTimeout(() => setIsInitialLoad(false), 100);
                        }
                    }
                } else if (res.status === 404) {
                    // Block doesn't exist, create it
                    await fetch('/api/blocks', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: blockId,
                            block_type: 'inline-social-outline',
                            content: { areas: [], columnWidths },
                            space_id: spaceId,
                            doc_id: docId
                        }),
                        credentials: 'include'
                    });
                    if (isMounted) {
                        setIsBlockLoading(false);
                        setTimeout(() => setIsInitialLoad(false), 100);
                    }
                }
            } catch (err) {
                console.error("Error loading social outline block", err);
                if (isMounted) {
                    setIsBlockLoading(false);
                    setTimeout(() => setIsInitialLoad(false), 100);
                }
            }
        };
        fetchBlock();
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockId]);

    // Check if the URL has a task ID that belongs to this component
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const taskIdFromUrl = urlParams.get('task');
        if (taskIdFromUrl && !openTaskId) {
            const hasTask = areas.some(a => a.tasks.some(t => t.taskId === taskIdFromUrl));
            if (hasTask) {
                setOpenTaskId(taskIdFromUrl);
            }
        }
    }, [areas, openTaskId]);

    const updateUrlTaskParam = (taskId) => {
        const url = new URL(window.location);
        if (taskId) {
            url.searchParams.set('task', taskId);
        } else {
            url.searchParams.delete('task');
        }
        window.history.replaceState({}, '', url);
    };

    // Persist state to API with debounce
    useEffect(() => {
        if (isBlockLoading || isInitialLoad) return;
        const timeout = setTimeout(async () => {
            try {
                const res = await fetch(`/api/blocks/${blockId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: { areas, columnWidths } }),
                    credentials: 'include'
                });
                if (res.ok) {
                    toast.success("Información guardada en base de datos");
                }
            } catch (err) {
                console.error("Error saving block", err);
            }
        }, 800);
        return () => clearTimeout(timeout);
    }, [areas, columnWidths, blockId, isBlockLoading, isInitialLoad, toast]);

    // Resizer logic
    const dragColRef = useRef(null);

    const startResize = (e, col) => {
        e.preventDefault();
        dragColRef.current = { col, startX: e.clientX, startWidth: columnWidths[col] };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!dragColRef.current) return;
        const { col, startX, startWidth } = dragColRef.current;
        const diffX = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + diffX);
        setColumnWidths(prev => ({ ...prev, [col]: newWidth }));
    };

    const handleMouseUp = () => {
        dragColRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (!e.target.closest('.iso-relative-btn') && !e.target.closest('.iso-dropdown')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const updateTask = (areaId, taskId, updater) => {
        setAreas(areas.map(a => {
            if (a.id === areaId) {
                return {
                    ...a,
                    tasks: a.tasks.map(t => t.id === taskId ? updater(t) : t)
                };
            }
            return a;
        }));
    };

    const handleAddArea = () => {
        if (!newAreaColor || !newAreaTitle.trim()) return;
        const newArea = {
            id: `area-${Date.now()}`,
            title: newAreaTitle,
            color: newAreaColor,
            tasks: []
        };
        setAreas([...areas, newArea]);
        setCreatingArea(false);
        setNewAreaColor(null);
        setNewAreaTitle('');
    };

    const handleTaskWizardComplete = (areaId, taskData) => {
        const newTask = {
            id: `task-${Date.now()}`,
            name: taskData.name,
            assignee: taskData.assignee,
            taskId: taskData.taskId,
            status: taskData.status,
            start_time: taskData.start_time,
            ads: [],
            formats: [],
            viral: false,
            socials: [],
            cta: []
        };
        setAreas(areas.map(a => {
            if (a.id === areaId) {
                return { ...a, tasks: [...a.tasks, newTask] };
            }
            return a;
        }));
        setActiveWizard(null);
    };

    const toggleSocial = (areaId, taskId, socialId) => {
        updateTask(areaId, taskId, t => ({
            ...t,
            socials: t.socials.includes(socialId) 
                ? t.socials.filter(s => s !== socialId)
                : [...t.socials, socialId]
        }));
    };

    const removeTag = (areaId, taskId, type, val) => {
        updateTask(areaId, taskId, t => {
            if (type === 'viral') return { ...t, viral: false };
            return { ...t, [type]: t[type].filter(v => v !== val) };
        });
    };

    const addTag = (areaId, taskId, type, val) => {
        updateTask(areaId, taskId, t => {
            if (type === 'viral') return { ...t, viral: true };
            if (!t[type].includes(val)) return { ...t, [type]: [...t[type], val] };
            if (!(t[type] || []).includes(val)) return { ...t, [type]: [...(t[type] || []), val] };
            return t;
        });
        setActiveDropdown(null);
    };

    const filteredAreas = areas.map(area => {
        if (!searchTerm) return { ...area, tasks: area.tasks || [] };
        const tasks = (area.tasks || []).filter(t => 
            (t.name || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        return { ...area, tasks };
    }).filter(area => (area.tasks || []).length > 0 || (area.title || '').toLowerCase().includes(searchTerm.toLowerCase()));

    let globalTaskIndex = 1;
    const areasWithIndices = filteredAreas.map(area => {
        const tasksWithIndex = (area.tasks || []).map(t => ({...t, globalIndex: globalTaskIndex++}));
        return { ...area, tasks: tasksWithIndex };
    });
    
    let totalPublications = globalTaskIndex - 1;
    let totalAreasCount = areasWithIndices.length;

    if (isBlockLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px', color: '#888' }}>
                <div className="spinner">Cargando outline...</div>
            </div>
        );
    }

    const itemsPerPage = 100;
    let totalItems = 0;
    areasWithIndices.forEach(a => {
        totalItems += Math.max(1, (a.tasks || []).length);
    });
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

    let currentItemCount = 0;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = currentPage * itemsPerPage;
    const paginatedAreas = [];

    for (const area of areasWithIndices) {
        if (currentItemCount >= endIndex) break;

        if ((area.tasks || []).length === 0) {
            if (currentItemCount >= startIndex && currentItemCount < endIndex) {
                paginatedAreas.push({ ...area, tasks: [] });
            }
            currentItemCount++;
            continue;
        }

        const areaTasks = [];
        const tLen = (area.tasks || []).length;
        for (let i = 0; i < tLen; i++) {
            if (currentItemCount >= startIndex && currentItemCount < endIndex) {
                areaTasks.push(area.tasks[i]);
            }
            currentItemCount++;
        }

        if (areaTasks.length > 0) {
            paginatedAreas.push({ ...area, tasks: areaTasks });
        }
    }

    return (
        <div 
            className="iso-container" 
            contentEditable={false} 
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
            position: 'relative',
            '--col-area-width': `${columnWidths.area}px`,
            '--col-pub-width': `${columnWidths.pub}px`,
            '--col-content-width': `${columnWidths.content}px`,
            '--col-social-width': `${columnWidths.social}px`,
            '--col-cta-width': `${columnWidths.cta}px`,
        }}>
            {isHovered && (
                <div style={{ position: 'absolute', top: '30px', left: '-28px', display: 'flex', flexDirection: 'column', gap: '5px', zIndex: 10, paddingRight: '15px' }}>
                    <button 
                        onClick={handleDeleteBlock} 
                        style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                        title="Eliminar bloque"
                    >
                        <Trash2 size={12} />
                    </button>
                    <button 
                        style={{ background: '#374151', color: '#9ca3af', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                        title="Proteger bloque (próximamente)"
                    >
                        <Lock size={12} />
                    </button>
                    <button 
                        onClick={() => {
                            navigator.clipboard.writeText(blockId);
                            toast.success("ID del bloque copiado");
                        }}
                        style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                        title="Copiar ID del bloque"
                    >
                        <Link size={12} />
                    </button>
                </div>
            )}
            
            <div className="iso-sticky-header-group" style={{ top: stickyOffset }}>
                <div className="iso-top-controls">
                    <div className="iso-header-top">
                        <div className="iso-tabs">
                            <button className="iso-tab active">General view</button>
                            <button className="iso-tab">Time order</button>
                        </div>
                        <div className="iso-search">
                            <Search size={14} className="iso-search-icon" />
                            <input 
                                type="text" 
                                placeholder="Buscar publicaciones..." 
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>
                    <div className="iso-header-divider"></div>
                </div>

                <div className="iso-table-header-wrapper" ref={headerScrollRef} style={{ overflowX: 'hidden' }}>
                    <div className="iso-table-header">
                            <div className="iso-col iso-col-area">
                                <span style={{background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px', marginRight: '6px', fontSize: '10px'}}>{totalAreasCount}</span> Area
                                <div className="iso-col-resizer" onMouseDown={e => startResize(e, 'area')}></div>
                            </div>
                            <div className="iso-col iso-col-pub">
                                <span style={{background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px', marginRight: '6px', fontSize: '10px'}}>{totalPublications}</span> Tipo de publicación
                                <div className="iso-col-resizer" onMouseDown={e => startResize(e, 'pub')}></div>
                            </div>
                            <div className="iso-col iso-col-content">
                                Tipo de contenido
                                <div className="iso-col-resizer" onMouseDown={e => startResize(e, 'content')}></div>
                            </div>
                            <div className="iso-col iso-col-social">
                                Social media
                                <div className="iso-col-resizer" onMouseDown={e => startResize(e, 'social')}></div>
                            </div>
                            <div className="iso-col iso-col-cta">
                                Call to action
                                <div className="iso-col-resizer" onMouseDown={e => startResize(e, 'cta')}></div>
                            </div>
                    </div>
                </div>
            </div>

            <div 
                className="iso-body" 
                onScroll={(e) => {
                    if (headerScrollRef.current) {
                        headerScrollRef.current.scrollLeft = e.target.scrollLeft;
                    }
                }}
            >
                <div className="iso-table-body">
                    {paginatedAreas.map(area => (
                        <div key={area.id} className="iso-area-row-group">
                            <div className="iso-col iso-col-area" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', paddingTop: '8px' }}>
                                <div className="iso-area-badge" style={{ background: (area.color || '').includes('gradient') ? area.color : (area.color || '#60a5fa') }}>
                                    <div className="iso-area-badge-text">{area.title || 'Área'}</div>
                                </div>
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Empty state or existing tasks */}
                                {(area.tasks || []).length === 0 && activeWizard !== area.id && (
                                    <div className="iso-task-row">
                                        <div className="iso-col iso-col-pub" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            {rapidTaskAreaId === area.id ? (
                                                <input 
                                                    autoFocus
                                                    type="text" 
                                                    placeholder="Título de la tarea rápida..."
                                                    value={rapidTaskTitle}
                                                    onChange={e => setRapidTaskTitle(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleRapidTaskSubmit(area.id);
                                                        if (e.key === 'Escape') { setRapidTaskAreaId(null); setRapidTaskTitle(''); }
                                                    }}
                                                    onBlur={() => handleRapidTaskSubmit(area.id)}
                                                    style={{ flex: 1, padding: '4px 8px', borderRadius: '4px', border: '1px solid #333', background: '#222', color: '#fff', fontSize: '13px' }}
                                                />
                                            ) : (
                                                <>
                                                    <button className="iso-btn-create" onClick={() => setActiveWizard(area.id)}>Crear</button>
                                                    {isAdmin && (
                                                        <button 
                                                            onClick={() => setRapidTaskAreaId(area.id)}
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(250, 204, 21, 0.1)', color: '#facc15', border: '1px solid rgba(250, 204, 21, 0.2)', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', transition: 'all 0.2s' }}
                                                            title="Crear tarea rápida en el espacio"
                                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(250, 204, 21, 0.2)'}
                                                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(250, 204, 21, 0.1)'}
                                                        >
                                                            <Zap size={14} style={{ marginRight: '2px' }}/>
                                                            <CheckSquare size={14} />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="iso-col iso-col-content"></div>
                                        <div className="iso-col iso-col-social"></div>
                                        <div className="iso-col iso-col-cta"></div>
                                    </div>
                                )}

                                {(area.tasks || []).map((task) => (
                                    <div key={task.id} className="iso-task-row">
                                        <div className="iso-col iso-col-pub" onClick={() => { 
                                            if (task.taskId) {
                                                updateUrlTaskParam(task.taskId);
                                                setOpenTaskId(task.taskId); 
                                            }
                                        }} style={{ cursor: 'pointer' }} title="Abrir tarea">
                                            <span style={{ fontSize: '10px', color: '#666', minWidth: '16px', textAlign: 'right', marginTop: '3px' }}>{task.globalIndex}.</span>
                                            <div className={`iso-checkbox ${task.status === 'completed' ? 'checked' : ''}`}>
                                                {task.status === 'completed' && <Check size={10} color="#fff" />}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span className="iso-task-name">{task.name}</span>
                                            </div>
                                        </div>
                                        <div className="iso-col iso-col-content">
                                            <div className="iso-tags-container">
                                                {(task.ads || []).map(ad => (
                                                    <span key={ad} className="iso-tag iso-tag-blue" onClick={() => removeTag(area.id, task.id, 'ads', ad)}>{ad}</span>
                                                ))}
                                                {(task.formats || []).map(fmt => (
                                                    <span key={fmt} className="iso-tag iso-tag-gray" onClick={() => removeTag(area.id, task.id, 'formats', fmt)}>{fmt}</span>
                                                ))}
                                                {task.viral && (
                                                    <span className="iso-tag iso-tag-pink" onClick={() => removeTag(area.id, task.id, 'viral', true)}>viralizacion</span>
                                                )}
                                            </div>
                                            
                                            <div className="iso-hover-actions">
                                                <div className="iso-relative-btn">
                                                    <button className="iso-btn-mini iso-btn-blue" onClick={() => setActiveDropdown({ areaId: area.id, taskId: task.id, type: 'ads' })}>+ red social</button>
                                                    {activeDropdown?.taskId === task.id && activeDropdown.type === 'ads' && (
                                                        <DropdownOptions options={ADS_OPTIONS.filter(o => !(task.ads || []).includes(o))} onSelect={(val) => addTag(area.id, task.id, 'ads', val)} />
                                                    )}
                                                </div>
                                                <div className="iso-relative-btn">
                                                    <button className="iso-btn-mini iso-btn-gray" onClick={() => setActiveDropdown({ areaId: area.id, taskId: task.id, type: 'formats' })}>+ formato</button>
                                                    {activeDropdown?.taskId === task.id && activeDropdown.type === 'formats' && (
                                                        <DropdownOptions options={FORMAT_OPTIONS.filter(o => !(task.formats || []).includes(o))} onSelect={(val) => addTag(area.id, task.id, 'formats', val)} />
                                                    )}
                                                </div>
                                                <div className="iso-relative-btn">
                                                    {!task.viral && (
                                                        <button className="iso-btn-mini iso-btn-pink" onClick={() => addTag(area.id, task.id, 'viral', true)}>+ viralizacion</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="iso-col iso-col-social">
                                            <div className="iso-social-icons">
                                                {SOCIAL_ICONS.map(soc => (
                                                    <button 
                                                        key={soc.id} 
                                                        className={`iso-social-btn ${(task.socials || []).includes(soc.id) ? 'active' : ''}`}
                                                        onClick={() => toggleSocial(area.id, task.id, soc.id)}
                                                        title={soc.id}
                                                    >
                                                        {soc.icon}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="iso-col iso-col-cta">
                                            <div className="iso-tags-container">
                                                {(task.cta || []).map(c => (
                                                    <span key={c} className="iso-tag iso-tag-dark" onClick={() => removeTag(area.id, task.id, 'cta', c)}>{c}</span>
                                                ))}
                                            </div>
                                            <div className="iso-relative-btn">
                                                <button className="iso-btn-add-cta" onClick={() => setActiveDropdown({ areaId: area.id, taskId: task.id, type: 'cta' })}>+</button>
                                                {activeDropdown?.taskId === task.id && activeDropdown.type === 'cta' && (
                                                    <DropdownOptions options={CTA_OPTIONS.filter(o => !(task.cta || []).includes(o))} onSelect={(val) => addTag(area.id, task.id, 'cta', val)} style={{ right: 0, left: 'auto' }} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {activeWizard === area.id && (
                                    <div className="iso-task-row">
                                        <div className="iso-col" style={{ flex: 1 }}>
                                            <TaskWizard
                                                spaceId={spaceId}
                                                toast={toast}
                                                onComplete={(data) => handleTaskWizardComplete(area.id, data)}
                                                onCancel={() => setActiveWizard(null)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {(area.tasks || []).length > 0 && activeWizard !== area.id && (
                                    <div className="iso-task-row">
                                        <div className="iso-col iso-col-pub" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            {rapidTaskAreaId === area.id ? (
                                                <input 
                                                    autoFocus
                                                    type="text" 
                                                    placeholder="Título de la tarea rápida..."
                                                    value={rapidTaskTitle}
                                                    onChange={e => setRapidTaskTitle(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleRapidTaskSubmit(area.id);
                                                        if (e.key === 'Escape') { setRapidTaskAreaId(null); setRapidTaskTitle(''); }
                                                    }}
                                                    onBlur={() => handleRapidTaskSubmit(area.id)}
                                                    style={{ flex: 1, padding: '4px 8px', borderRadius: '4px', border: '1px solid #333', background: '#222', color: '#fff', fontSize: '13px' }}
                                                />
                                            ) : (
                                                <>
                                                    <button className="iso-btn-create" onClick={() => setActiveWizard(area.id)}>Crear</button>
                                                    {isAdmin && (
                                                        <button 
                                                            onClick={() => setRapidTaskAreaId(area.id)}
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(250, 204, 21, 0.1)', color: '#facc15', border: '1px solid rgba(250, 204, 21, 0.2)', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', transition: 'all 0.2s' }}
                                                            title="Crear tarea rápida en el espacio"
                                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(250, 204, 21, 0.2)'}
                                                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(250, 204, 21, 0.1)'}
                                                        >
                                                            <Zap size={14} style={{ marginRight: '2px' }}/>
                                                            <CheckSquare size={14} />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="iso-col iso-col-content"></div>
                                        <div className="iso-col iso-col-social"></div>
                                        <div className="iso-col iso-col-cta"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="iso-footer">
                    <div className="iso-footer-actions">
                        {creatingArea ? (
                            <div className="iso-create-area">
                                {!newAreaColor ? (
                                    <div className="iso-color-picker">
                                        {COLORS.map(c => (
                                            <button 
                                                key={c.id} 
                                                className="iso-color-btn" 
                                                style={{ background: c.hex }}
                                                onClick={() => { setNewAreaColor(c.hex); setTimeout(() => areaInputRef.current?.focus(), 50); }}
                                                title={c.id}
                                            />
                                        ))}
                                        <button className="iso-cancel-btn" onClick={() => setCreatingArea(false)}>Cancelar</button>
                                    </div>
                                ) : (
                                    <div className="iso-title-input">
                                        <div className="iso-area-badge" style={{ background: newAreaColor, minWidth: '20px' }}></div>
                                        <input 
                                            ref={areaInputRef}
                                            type="text" 
                                            placeholder="Nombre del área..." 
                                            value={newAreaTitle}
                                            onChange={e => setNewAreaTitle(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') handleAddArea();
                                                if (e.key === 'Escape') { setNewAreaColor(null); setCreatingArea(false); }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button className="iso-add-area-btn" onClick={() => setCreatingArea(true)}>
                                + Agregar área
                            </button>
                        )}

                        {totalPages > 1 && (
                            <div className="iso-pagination">
                                <button 
                                    className="iso-pagination-btn" 
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    Anterior
                                </button>
                                <span>Página {currentPage} de {totalPages}</span>
                                <button 
                                    className="iso-pagination-btn" 
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {openTaskId && (
                <React.Suspense fallback={null}>
                        <ComponentTaskHoy user={currentUser}
                            standaloneTaskId={openTaskId}
                            onCloseStandalone={() => {
                                updateUrlTaskParam(null);
                                setOpenTaskId(null);
                            }} 
                        />
                </React.Suspense>
            )}
        </div>
    );
};

export default InlineSocialOutline;
